import { type FormControlProps } from '@mui/base'
import { type FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { FormControl, FormLabel, Option, Select } from 'base-ui'

import {
  ProductVolumePricingCompactDiscountBadge,
  ProductVolumePricingExtendedDiscountBadge,
} from '../../../../../product'
import { useProductMaxSelectableQuantity } from '../../../../product-order-limit/useProductMaxSelectableQuantity'
import { useProductPageContext } from '../../../productPageContext'
import { useShouldRenderVolumePricingLabel } from '../../../ProductPageVolumePricingBadge/useShouldRenderVolumePricingBadge'
import { useBuildAddToCartParams } from '../ProductAddToCartCta/useBuildAddToCartParams'

const useSelectionOptions = (): {
  label: string
  value: number
}[] => {
  const { currentOffer, product } = useProductPageContext()

  const maxSelectableQuantity = useProductMaxSelectableQuantity({
    offer: currentOffer,
    product,
  })

  const selectOptions = useMemo(() => {
    const options: { label: string; value: number }[] = []

    for (let index = 1; index <= maxSelectableQuantity; index += 1) {
      options.push({ label: index.toString(), value: index })
    }

    return options
  }, [maxSelectableQuantity])

  return selectOptions
}

export const ProductAddToCartQuantitySelection: FC<FormControlProps> = (
  props,
) => {
  const selectOptions = useSelectionOptions()
  const { quantitySelectionControls } = useProductPageContext()
  const { disableCta } = useBuildAddToCartParams()
  const renderVolumeBasedPricingLabel = useShouldRenderVolumePricingLabel()

  return (
    <FormControl
      {...props}
      disabled={disableCta}
      value={quantitySelectionControls.quantity}
    >
      <FormLabel htmlFor="quantity" id="quantity-label">
        <FormattedMessage id="general.label.quantity" />
      </FormLabel>
      <Select
        aria-labelledby="quantity-label"
        data-qa-id="product-quantity-select"
        id="quantity"
        onChange={(event, value): void => {
          if (!value) {
            return
          }
          quantitySelectionControls.setQuantity(value)
        }}
        value={quantitySelectionControls.quantity}
      >
        {selectOptions.map((option) => (
          <Option
            data-qa-id="product-quantity-select-option"
            data-value={option.value}
            key={option.value}
            label={option.label}
            value={option.value}
          >
            {option.label}
            {renderVolumeBasedPricingLabel ? (
              <>
                <ProductVolumePricingCompactDiscountBadge
                  className="hidden tablet:flex"
                  selectedValue={option.value}
                />
                <ProductVolumePricingExtendedDiscountBadge
                  selectedValue={option.value}
                />
              </>
            ) : null}
          </Option>
        ))}
      </Select>
    </FormControl>
  )
}
